import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { ShieldCheckIcon, ExclamationTriangleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { get } from 'lodash';
import moment from 'moment';

const AutoMailerUserNotification = ({ notification }) => {
  const intl = useIntl();

  const getWeekMessage = (week_number) => {
    switch (week_number) {
      case 0:
        return intl.formatMessage({ id: 'succession.notifications.auto_mailer_user.week_0_message' });
      case 1:
        return intl.formatMessage({ id: 'succession.notifications.auto_mailer_user.week_1_message' });
      case 2:
        return intl.formatMessage({ id: 'succession.notifications.auto_mailer_user.week_2_message' });
      default:
        return intl.formatMessage({ id: 'succession.notifications.auto_mailer_user.default_message' });
    }
  };
  const dataString = get(notification, 'data', '{}');
  let data = {};
  
  try {
    data = JSON.parse(dataString);
  } catch (error) {
    console.warn('Failed to parse notification data:', error);
  }

  const weekNumber = get(data, 'week', 0);
  const isRead = !!get(notification, 'readAt');
  const readTime = get(notification, 'readAt');

  // Get appropriate icon and colors based on week - now using primary color consistently
  const getWeekStyles = (week) => {
    // Use the same primary color for all weeks, with different icons
    const icon = week === 0 ? ExclamationTriangleIcon : ShieldCheckIcon;
    
    return {
      icon,
      bgColor: isRead ? 'bg-succession-bg-tertiary' : 'bg-succession-primary-10',
      borderColor: isRead ? 'border-succession-border' : 'border-succession-primary',
      iconBg: isRead ? 'bg-succession-bg-secondary' : 'bg-succession-primary',
      iconColor: isRead ? 'text-succession-text-muted' : 'text-white',
      badgeColor: isRead ? 'bg-succession-bg-secondary text-succession-text-muted' : 'bg-succession-primary-20 text-succession-primary'
    };
  };

  const styles = getWeekStyles(weekNumber);
  const IconComponent = styles.icon;

  return (
    <div className={`p-4 border-l-4 ${styles.borderColor} ${styles.bgColor} hover:shadow-md transition-shadow duration-200`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          {/* Icon */}
          <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${styles.iconBg}`}>
            <IconComponent className={`w-5 h-5 ${styles.iconColor}`} />
          </div>
          
          {/* Week Badge */}
          <div className={`px-3 py-1 rounded-full text-sm font-bold ${styles.badgeColor}`}>
            {intl.formatMessage({ id: 'succession.notifications.auto_mailer_user.week_label' }, { week: weekNumber + 1 })}
          </div>
        </div>

        {/* Unread indicator */}
        {!isRead && (
          <div className="w-3 h-3 bg-succession-primary rounded-full flex-shrink-0"></div>
        )}
      </div>

      {/* Content */}
      <div className="mb-4">
        <p className={`text-base font-medium mb-2 ${isRead ? 'text-succession-text-secondary' : 'text-succession-text-primary'}`}>
          {getWeekMessage(weekNumber)}
        </p>

        <p className={`text-sm ${isRead ? 'text-succession-text-muted' : 'text-succession-text-secondary'}`}>
          {intl.formatMessage({ id: 'succession.notifications.auto_mailer_user.subtitle' })}
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <a
          href="/succession/bcp"
          className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
            isRead 
              ? 'bg-succession-bg-secondary text-succession-text-secondary hover:bg-succession-hover-bg' 
              : 'bg-succession-primary text-white hover:bg-succession-primary-hover'
          }`}
        >
          <ShieldCheckIcon className="w-4 h-4 mr-2" />
          {intl.formatMessage({ id: 'succession.notifications.auto_mailer_user.visit_builder' })}
        </a>

        {/* Timestamp */}
        <div className="flex items-center text-xs text-succession-text-muted">
          {readTime && (
            <>
              <ClockIcon className="w-3 h-3 mr-1" />
              <span>{intl.formatMessage({ id: 'succession.notifications.auto_mailer_user.read_at' }, { time: moment(readTime).fromNow() })}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

AutoMailerUserNotification.propTypes = {
  notification: PropTypes.shape({
    data: PropTypes.string,
    readAt: PropTypes.string,
  }).isRequired,
};

export default AutoMailerUserNotification; 